// # Revenue Overview Component
import React from 'react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './bnct_revenue_overview.css';

// # Chart colors
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const RevenueOverview = ({ revenueData, member }) => {
  // # Check if data is available
  if (!revenueData) return <div className="bnct_loading">Loading financial data...</div>;

  // # Determine if this is a project budget account
  const isProjectBudget = member.id === 'project-budget';
  
  // # Use stored values directly from the updated data structure
  // # Total revenue is now the gross amount (before budget)
  const totalRevenue = revenueData.totalRevenue || 0;
  
  // # Net revenue is after budget deduction
  const netRevenue = revenueData.netRevenue || 0;
  
  // # Budget allocation is the difference between total and net for team members
  // # For budget account, it's already stored in budgetAllocation
  const budgetAllocation = isProjectBudget ? 
    0 : // For budget account, don't show budget allocation in this calculation
    totalRevenue - netRevenue; // For team members, calculate from total and net
  
  // # Available cash is net revenue minus salaries for team members
  // # For budget account, it's net revenue minus expenses (already calculated)
  const availableCash = revenueData.availableCash || 0;

  return (
    <div className="bnct_overview-container">
      {/* # Stats grid showing key financial metrics */}
      <div className="bnct_stats-grid">
        <div className="bnct_stat-card total-revenue">
          <h3>Total Revenue</h3>
          <p className="bnct_stat-value">E£ {totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="bnct_stat-description">Total revenue before budget allocation</p>
        </div>
        
        {/* # Budget allocation card - only show for team members */}
        {!isProjectBudget && (
          <div className="bnct_stat-card budget-allocation">
            <h3>Budget Allocation (5%)</h3>
            <p className="bnct_stat-value">E£ {budgetAllocation.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="bnct_stat-description">Amount allocated to project budget</p>
          </div>
        )}
        
        {/* # Only show expenses for project budget account */}
        {isProjectBudget && (
          <div className="bnct_stat-card expenses">
            <h3>Total Expenses</h3>
            <p className="bnct_stat-value">E£ {(revenueData.expenseShare || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="bnct_stat-description">Total expenses deducted from budget</p>
          </div>
        )}
        
        {/* # Only show salaries for team members, not for project budget */}
        {!isProjectBudget && (
          <div className="bnct_stat-card salaries">
            <h3>Total Salaries Deducted</h3>
            <p className="bnct_stat-value">E£ {(revenueData.salariesDeducted || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="bnct_stat-description">Salaries deducted from net revenue</p>
          </div>
        )}
        
        <div className="bnct_stat-card net-revenue">
          <h3>Net Revenue</h3>
          <p className="bnct_stat-value">E£ {netRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="bnct_stat-description">Revenue after budget allocation</p>
        </div>
        
        <div className="bnct_stat-card available-cash">
          <h3>Available Cash</h3>
          <p className="bnct_stat-value">E£ {availableCash.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="bnct_stat-description">{isProjectBudget ? 'Available budget after expenses' : 'Available cash after salary deductions'}</p>
        </div>
      </div>

      {/* # Revenue Distribution Chart - Shows different data for team members vs budget */}
      <div className="bnct_chart-container">
        <h3>Revenue Distribution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={isProjectBudget ? 
                [
                  { name: 'Available Cash', value: revenueData.availableCash || 0 },
                  { name: 'Expenses', value: revenueData.expenseShare || 0 }
                ] : 
                [
                  { name: 'Available Cash', value: revenueData.availableCash || 0 },
                  { name: 'Salaries Deducted', value: revenueData.salariesDeducted || 0 },
                  { name: 'Budget Allocation', value: budgetAllocation || 0 }
                ]}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label
            >
              {/* # Apply different colors to each segment */}
              {COLORS.map((color, index) => (
                <Cell key={`cell-${index}`} fill={color} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => `E£ ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RevenueOverview; 